import React from 'react'
import { useLocalStorage } from 'react-use'

// Types
import { StorageKey, localStorageKeys } from '@sketch/constants'

// Components
import {
  CloseIconContainer,
  NewFeatureHighlightContainerHeader,
  NewFeatureHighlightContainerSidebar,
  StyledLinkButton,
} from './NewFeatureHighlightPopover.styles'

import NewFeatureHighlight from '../NewFeatureHighlight'
import NewFeatureHighlightPopoverBody from '../NewFeatureHighlightPopoverBody'

import {
  Box,
  Flex,
  Popover,
  Button,
  ButtonUnstyled,
  ButtonVariant,
  LinkButtonProps,
} from '@sketch/components'

//Icons
import { ReactComponent as CloseIcon } from '@sketch/icons/cross-16-svg'

type FeaturePopoverLocations = 'header' | 'sidebar'

interface NewFeaturePopoverParams {
  placement: string
  spacing: number
  dotWrapper: React.FunctionComponent<any>
}

export interface NewFeatureHighlightPopoverProps {
  onConfirm?: () => void
  highlightPlacement: FeaturePopoverLocations
  titleText: string
  bodyText: string
  confirmCopy?: string
  buttonVariant?: ButtonVariant
  buttonLinkProps?: LinkButtonProps
  localStorageKey: StorageKey
  closeButtonTestId?: string
  show?: boolean
}

const POPOVER_PARAMS: {
  [key in FeaturePopoverLocations]: NewFeaturePopoverParams
} = {
  header: {
    placement: 'bottom-start',
    spacing: 0,
    dotWrapper: NewFeatureHighlightContainerHeader,
  },
  sidebar: {
    placement: 'right-start',
    spacing: 0,
    dotWrapper: NewFeatureHighlightContainerSidebar,
  },
}

const NewFeatureHighlightPopover: React.FC<NewFeatureHighlightPopoverProps> = ({
  onConfirm,
  confirmCopy,
  highlightPlacement,
  closeButtonTestId = 'new-feature-hightlight-close',
  buttonLinkProps,
  titleText,
  bodyText,
  localStorageKey,
  show = true,
  children,
}) => {
  const [introductionSeen, setIntroductionSeen] = useLocalStorage(
    localStorageKeys[localStorageKey],
    false
  )

  const { placement, spacing, dotWrapper: WrapperComponent } = POPOVER_PARAMS[
    highlightPlacement
  ]

  const closePopover = () => setIntroductionSeen(true)

  if (introductionSeen || !show) {
    return <>{children}</>
  }

  const onConfirmClick = () => {
    closePopover()
    onConfirm?.()
  }

  const getButton = () => {
    if (buttonLinkProps) {
      return (
        <StyledLinkButton
          variant="secondary"
          size="24"
          type="button"
          {...buttonLinkProps}
        >
          {buttonLinkProps.children}
        </StyledLinkButton>
      )
    } else {
      return (
        <Button variant="primary" size="24" onClick={onConfirmClick}>
          {confirmCopy}
        </Button>
      )
    }
  }

  return (
    <Popover
      placement={placement}
      spacing={`${spacing}px`}
      popup={
        <Box p={4} width={262}>
          <WrapperComponent>
            <NewFeatureHighlight />
          </WrapperComponent>
          <CloseIconContainer>
            <ButtonUnstyled
              onClick={closePopover}
              data-testid={closeButtonTestId}
            >
              <CloseIcon width="16px" />
            </ButtonUnstyled>
          </CloseIconContainer>
          <NewFeatureHighlightPopoverBody
            titleText={titleText}
            bodyText={bodyText}
          />
          <Flex mt={4} justifyContent="flex-end">
            {getButton()}
          </Flex>
        </Box>
      }
      visible={!introductionSeen}
    >
      {children}
    </Popover>
  )
}

export default NewFeatureHighlightPopover
